import { Furniture } from "@/actors/furniture"; // Assuming you have a Furniture class for handling furniture objects

export class FurnitureManager {
  private furnitures: Map<number, any>; // Stores furniture data with their IDs
  scene: any;
  fakeFurniture: Furniture | null = null;

  constructor(scene: any) {
    this.furnitures = new Map();
    this.scene = scene as any; // Start furniture IDs from 1
  }

  /**
   * Adds a new furniture item.
   * @param data - The furniture data.
   * @returns The ID assigned to the furniture.
   */
  public add(data: {
    position: any;
    id: number;
    x: number;
    y: number;
    name: string;
    slug: string;
    canSit: boolean;
    isPlaying: boolean;
    direction: number;
    stackLevel?: number;
    color?: string; // Additional data
    material?: string; // Additional data
    isMovable?: boolean; // Additional data
    occupiedPosition: any;
  }) {
    // Create a new Furniture instance
    const furniture = new Furniture({
      id: data.id,
      x: data.position.x,
      y: data.position.y,
      slug: data.slug,
      canSit: data.canSit,
      direction: data.direction, // this can be undefined or passed as data.direction
      stackLevel: data.stackLevel || 0, // stackLevel can be set to 0 if it's falsy
      isPlaying: data.isPlaying,
    });

    // Prepare the object to store in the map
    const furnitureData = {
      instance: furniture, // The Furniture instance
      name: data.name, // Additional metadata
      direction: data.direction,
      canSit: data.canSit,
      occupiedPosition: data.occupiedPosition,
    };

    this.furnitures.set(data.id, furnitureData);

    return furniture;
  }

  /**
   * Removes a furniture by its ID.
   * @param id - The ID of the furniture to remove.
   * @returns True if the furniture was removed, false if not found.
   */
  public removeById(id: number) {
    const furniture = this.furnitures.get(id);
    if (furniture) {
      // Optionally, handle any clean-up for the furniture
      this.furnitures.delete(id);
      return furniture;
    }
    return false;
  }

  /**
   * Retrieves all furniture items.
   * @returns An array of all furniture items.
   */

  /**
   * Retrieves a furniture by its ID.
   * @param id - The ID of the furniture.
   * @returns The furniture, or undefined if not found.
   */
  public getById(id: number): Furniture | undefined {
    console.log(this.furnitures, 'ff')
    return this.furnitures.get(id);
  }

  public getAllFurniture(): Furniture[] {
    return Array.from(this.furnitures.values());
  }

  public getByPosition(x: number, y: number) {
    const matchingFurnitures: any = [];

    for (const furniture of this.furnitures.values()) {
      const { occupiedPosition, direction } = furniture;

      if (occupiedPosition) {
        let positionsToCheck: any = [];

        // Determine which array to check based on direction
        if (direction === 1 || direction === 6) {
          // Assume 0 = horizontal
          positionsToCheck = occupiedPosition.horizontal || [];
        } else if (direction === 2 || direction === 4) {
          // Assume 1 = vertical
          positionsToCheck = occupiedPosition.vertical || [];
        } else {
          // If direction is undefined or unexpected, check both
          positionsToCheck = [
            ...(occupiedPosition.horizontal || []),
            ...(occupiedPosition.vertical || []),
          ];
        }

        // Check if any position matches the given x and y
        const isOccupied = positionsToCheck.some(
          (pos: { x: number; y: number }) => pos.x === x && pos.y === y
        );

        if (isOccupied) {
          matchingFurnitures.push(furniture);
        }
      }
    }

    return matchingFurnitures; // Return an array of all matching furniture instances
  }

  public createFakeFurniture({
    x,
    y,
    slug,
  }: {
    x: number;
    y: number;
    slug: string;
  }) {
    const fakeFurniture = new Furniture({
      id: 0,
      x: null,
      y: null,
      slug,
      canSit: false,
      direction: 0,
      isPlaying: false,
    });

    fakeFurniture.setFake();
    fakeFurniture.move(x, y, this.scene);
    this.scene.add(fakeFurniture);

    this.fakeFurniture = fakeFurniture;
  }

  removeFakeFurniture() {
    this.scene?.remove(this.fakeFurniture);
  }

  /**
   * Updates the occupiedPosition of a furniture item.
   * @param id - The ID of the furniture to update.
   * @param newOccupiedPosition - The new occupiedPosition value.
   * @returns True if the update was successful, false if furniture not found.
   */
  public updateOccupiedPosition(id: number, newOccupiedPosition: any): boolean {
    const furnitureData = this.furnitures.get(id);
    if (!furnitureData) {
      return false; // Furniture not found
    }

    // Update the occupiedPosition in the metadata
    furnitureData.occupiedPosition = newOccupiedPosition;

    return true; // Update successful
  }
}
